/**
 * Achievements (EPIC D6, docs/01 achievements). Each one is earned once,
 * persists in core/meta.ts and unlocks something small and real: a starting
 * weapon choice, a card entering the shop pool, or ENDLESS mode. Names and
 * descriptions: i18n `ach.<id>.name` / `ach.<id>.desc`.
 */
export type AchievementReward =
  | { readonly kind: 'weapon'; readonly id: string }
  | { readonly kind: 'card'; readonly id: string }
  | { readonly kind: 'endless' };

export interface AchievementDef {
  readonly id: string;
  /** Short badge glyph (sector select + victory screen) */
  readonly glyph: string;
  readonly rewards: ReadonlyArray<AchievementReward>;
}

export const ACHIEVEMENTS: ReadonlyArray<AchievementDef> = [
  // Sector clears (each) — the next sector unlocks with them
  { id: 'clear_s1', glyph: 'I', rewards: [{ kind: 'weapon', id: 'smg' }] },
  { id: 'clear_s2', glyph: 'II', rewards: [{ kind: 'weapon', id: 'plasma_orb' }] },
  { id: 'clear_s3', glyph: 'III', rewards: [{ kind: 'weapon', id: 'needle_laser' }] },
  { id: 'clear_s4', glyph: 'IV', rewards: [{ kind: 'endless' }, { kind: 'weapon', id: 'mortar' }] },
  // Skill
  { id: 'untouchable', glyph: '◇', rewards: [{ kind: 'card', id: 'ghostdash' }] },
  { id: 'deathless', glyph: '♥', rewards: [{ kind: 'card', id: 'superfan' }] },
  { id: 'blitz', glyph: '»', rewards: [{ kind: 'card', id: 'overcharge' }] },
  // Build
  { id: 'arsenal', glyph: '≡', rewards: [{ kind: 'card', id: 'apex' }] },
  { id: 'topgun', glyph: '▲', rewards: [{ kind: 'card', id: 'lifesteal' }] },
  { id: 'collector', glyph: '✦', rewards: [{ kind: 'card', id: 'luckychip' }] },
];

/** Thresholds of the condition-based achievements. */
export const ACHIEVEMENT_RULES = {
  /** blitz: clear a sector within this much fight time (s) */
  blitzSeconds: 360,
  /** arsenal: weapons active at once */
  arsenalWeapons: 3,
  /** topgun: ship-wide gun tier reached */
  topgunTier: 4,
  /** collector: cards owned in one sector run */
  collectorCards: 12,
} as const;

/** Achievement id for clearing sector `index` (0-based). */
export function clearAchievementId(index: number): string {
  return `clear_s${index + 1}`;
}
